import { useScreenSize } from "@/providers/ScreenSizeProvider";
import {
  formatDateAndMonth,
  getUTCWeekPeriod,
  UTCToLocalDate,
  UTCWeekPeriod,
} from "@/utils/dates";
import niceScale from "@/utils/nice-scale";
import { formatNumber } from "@/utils/number-utils";
import { isOneOf } from "@/utils/type-guards";
import { UTCDate } from "@date-fns/utc";
import { addWeeks, differenceInWeeks, startOfWeek } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeftIcon, ChevronRightIcon, Loader } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button as RACButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { DashedRings } from "../DashedRings";
import { composeTailwindRenderProps, focusRing } from "../generic/utils";

export type WeeklyDataPoint = {
  date: UTCDate;
  value: number;
  dailyBurnRate?: number;
};

export type GraphBarProps<T extends WeeklyDataPoint> = {
  week: UTCWeekPeriod;
  data: T;
  maxValue: number;
  index: number;
};

const BAR_WIDTH = 44;
const GRAPH_HEIGHT = BAR_WIDTH * 5;

export function WeeklyGraph<T extends WeeklyDataPoint>({
  data,
  alignment = "center",
  baseDate,
  minDate,
  maxDate,
  maxValue,
  isLoading = false,
  loadingText = "Henter...",
  hasData = true,
  noDataText = "Intet data",
  showCurrentWeekCursor = true,
  showTrendLine = false,
  onVisibleWeeksChange,
  renderBar,
}: {
  data: T[];
  alignment?: "center" | "left" | "right";
  baseDate?: UTCDate;
  minDate?: UTCDate;
  maxDate?: UTCDate;
  maxValue?: number;
  isLoading?: boolean;
  loadingText?: string;
  hasData?: boolean;
  noDataText?: string;
  showCurrentWeekCursor?: boolean;
  showTrendLine?: boolean;
  onVisibleWeeksChange?: (visibleWeeks: UTCWeekPeriod[]) => void;
  renderBar: (props: GraphBarProps<T>) => React.ReactNode;
}) {
  const { isXLBreakpoint } = useScreenSize();

  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [resizeCounter, setResizeCounter] = useState(0);

  const barCount = useMemo(
    () => Math.floor(containerWidth / BAR_WIDTH),
    [containerWidth],
  );

  const currentDate = useMemo(() => new UTCDate(), []);
  baseDate = useMemo(() => baseDate ?? currentDate, [baseDate, currentDate]);

  const currentWeek = useMemo(
    () => getUTCWeekPeriod(currentDate),
    [currentDate],
  );

  const baseWeek = useMemo(() => getUTCWeekPeriod(baseDate), [baseDate]);

  const minWeek = useMemo(
    () => minDate && getUTCWeekPeriod(minDate),
    [minDate],
  );

  const maxWeek = useMemo(
    () => maxDate && getUTCWeekPeriod(maxDate),
    [maxDate],
  );

  const minWeekOffset = useMemo(() => {
    if (!minWeek) return;
    return Math.min(
      differenceInWeeks(minWeek.start, baseWeek.start, {
        roundingMethod: "ceil",
      }),
      0,
    );
  }, [baseWeek.start, minWeek]);

  const maxWeekOffset = useMemo(() => {
    if (!maxWeek) return;
    return Math.max(
      differenceInWeeks(maxWeek.start, baseWeek.start, {
        roundingMethod: "ceil",
      }),
      0,
    );
  }, [baseWeek.start, maxWeek]);

  const baseWeekOffsets = useMemo(() => {
    let weekOffset = 0;
    let firstWeekOffset = 0;
    let lastWeekOffset = 0;

    if (barCount) {
      let alignmentOffset = 0;
      if (alignment === "left") {
        alignmentOffset = 0;
      } else if (alignment === "center") {
        alignmentOffset = -Math.floor(barCount / 2);
      } else if (alignment === "right") {
        alignmentOffset = -barCount + 1;
      }

      firstWeekOffset = alignmentOffset;
      lastWeekOffset = alignmentOffset + barCount - 1;

      const minOffsetOverflow =
        minWeekOffset !== undefined
          ? Math.max(minWeekOffset - firstWeekOffset, 0)
          : 0;

      const maxOffsetOverflow =
        maxWeekOffset !== undefined
          ? Math.max(lastWeekOffset - maxWeekOffset, 0)
          : 0;

      if (minOffsetOverflow || maxOffsetOverflow) {
        let adjustment = 0;
        if (isOneOf(alignment, "center", "left"))
          adjustment += minOffsetOverflow;
        if (isOneOf(alignment, "center", "right"))
          adjustment -= maxOffsetOverflow;

        weekOffset += adjustment;
        firstWeekOffset += adjustment;
        lastWeekOffset += adjustment;
      }
    }

    return { weekOffset, firstWeekOffset, lastWeekOffset };
  }, [alignment, barCount, maxWeekOffset, minWeekOffset]);

  const [weekOffset, setWeekOffset] = useState(0);

  const canGoBack = useMemo(
    () => minWeekOffset === undefined || weekOffset > minWeekOffset,
    [minWeekOffset, weekOffset],
  );

  const canGoForward = useMemo(
    () => maxWeekOffset === undefined || weekOffset < maxWeekOffset,
    [maxWeekOffset, weekOffset],
  );

  const visibleWeeks = useMemo(() => {
    if (barCount <= 0) return [];

    return Array.from({ length: barCount }, (_, i) =>
      getUTCWeekPeriod(
        addWeeks(
          baseWeek.start,
          baseWeekOffsets.firstWeekOffset + weekOffset + i,
        ),
      ),
    );
  }, [barCount, baseWeek.start, baseWeekOffsets.firstWeekOffset, weekOffset]);

  const {
    firstWeekIndex,
    lastWeekIndex,
    currentWeekIndex,
    previousWeekIndex,
    nextWeekIndex,
  } = useMemo(() => {
    const indices = {
      firstWeekIndex: null as number | null,
      lastWeekIndex: null as number | null,
      currentWeekIndex: null as number | null,
      previousWeekIndex: null as number | null,
      nextWeekIndex: null as number | null,
    };

    if (visibleWeeks.length) {
      indices.firstWeekIndex = 0;
      indices.lastWeekIndex = visibleWeeks.length - 1;

      const currentWeekIndex = visibleWeeks.findIndex(
        (week) => week.start.getTime() === currentWeek.start.getTime(),
      );

      if (currentWeekIndex !== -1) {
        indices.currentWeekIndex = currentWeekIndex;
        indices.previousWeekIndex =
          currentWeekIndex > 0 ? currentWeekIndex - 1 : null;
        indices.nextWeekIndex =
          currentWeekIndex < visibleWeeks.length - 1
            ? currentWeekIndex + 1
            : null;
      }
    }

    return indices;
  }, [visibleWeeks, currentWeek.start]);

  const visibleData = useMemo(() => {
    if (!visibleWeeks.length || !data.length) return [];

    const firstWeekStart = visibleWeeks[0].start;
    const lastWeekEnd = visibleWeeks[visibleWeeks.length - 1].end;

    const matchingData = data.filter((point) => {
      return point.date >= firstWeekStart && point.date <= lastWeekEnd;
    });

    const weekDataMap = new Map<string, T[]>();
    matchingData.forEach((point) => {
      const weekKey = getUTCWeekPeriod(
        new UTCDate(point.date),
      ).start.toISOString();
      let weekData = weekDataMap.get(weekKey);
      if (!weekData) {
        weekData = [];
        weekDataMap.set(weekKey, weekData);
      }
      weekData.push(point);
    });

    return visibleWeeks.map((week) => {
      const weekKey = week.start.toISOString();
      const weekDataArray = weekDataMap.get(weekKey);
      return weekDataArray && weekDataArray.length > 0
        ? weekDataArray[0]
        : null;
    });
  }, [visibleWeeks, data]);

  const bars = useMemo(() => {
    return visibleWeeks.map((week, index) => {
      let barStartDate = week.start;
      let barEndDate = week.end;

      if (minDate && week.start < minDate && week.end > minDate) {
        barStartDate = minDate;
      }

      if (maxDate && week.start < maxDate && week.end > maxDate) {
        barEndDate = maxDate;
      }

      const formattedStartDate = formatDateAndMonth(
        UTCToLocalDate(barStartDate),
      );
      const formattedEndDate = formatDateAndMonth(UTCToLocalDate(barEndDate));
      const formattedPeriod = `${formattedStartDate} - ${formattedEndDate}`;

      return {
        week,
        formattedStartDate,
        formattedEndDate,
        formattedPeriod,
        isFirstWeek: index === firstWeekIndex,
        isLastWeek: index === lastWeekIndex,
        isCurrentWeek: index === currentWeekIndex,
        isPreviousWeek: index === previousWeekIndex,
        isNextWeek: index === nextWeekIndex,
        data: visibleData[index] || null,
      };
    });
  }, [
    visibleWeeks,
    minDate,
    maxDate,
    firstWeekIndex,
    lastWeekIndex,
    currentWeekIndex,
    previousWeekIndex,
    nextWeekIndex,
    visibleData,
  ]);

  maxValue = useMemo(
    () => maxValue ?? Math.max(...data.map((point) => point.value), 0),
    [data, maxValue],
  );

  const niceMaxValue = useMemo(
    () =>
      niceScale([0, maxValue !== 0 ? maxValue * 1.1 : 1000], {
        minTicks: 3,
        maxTicks: 7,
      }).niceMax,
    [maxValue],
  );

  const maxVisibleValue = useMemo(
    () =>
      Math.max(
        ...visibleData
          .filter((point) => point !== null)
          .map((point) => point!.value),
        0,
      ),
    [visibleData],
  );

  const { niceMax: niceMaxVisibleValue, ticks } = useMemo(() => {
    const { niceMax, ticks } = niceScale(
      [0, maxVisibleValue > 0 ? maxVisibleValue * 1.1 : 1000],
      {
        minTicks: 3,
        maxTicks: 7,
      },
    );

    return {
      niceMax,
      ticks: [...ticks].reverse(),
    };
  }, [maxVisibleValue]);

  const trendLinePathData = useMemo(() => {
    if (!showTrendLine || visibleData.length === 0) {
      return { area: "", line: "", totalWidth: 0, points: [] };
    }

    const height = GRAPH_HEIGHT;
    const filteredData = visibleData.filter((item) => item !== null);

    if (filteredData.length === 0) {
      return { area: "", line: "", totalWidth: 0, points: [] };
    }

    const maxTrendValue = Math.max(
      ...visibleData.map((point) => (point?.dailyBurnRate as number) || 0),
    );

    if (maxTrendValue === 0) {
      return { area: "", line: "", totalWidth: 0, points: [] };
    }

    const actualStartWeek = visibleWeeks[0]
      ? startOfWeek(visibleWeeks[0].start, { weekStartsOn: 1 })
      : new UTCDate();

    const points = filteredData.map((point) => {
      const pointWeek = startOfWeek(point!.date, { weekStartsOn: 1 });
      const weeksFromStart = differenceInWeeks(pointWeek, actualStartWeek);

      const pointValue = point?.dailyBurnRate || 0;

      let x: number;

      const totalPossibleBars = bars.length;

      const barWidth = containerWidth / totalPossibleBars;

      const spacing = barWidth;
      const barPosition = weeksFromStart * spacing;

      // eslint-disable-next-line prefer-const
      x = barPosition + barWidth / 2;

      return {
        x: x,
        y: height - (pointValue / niceMaxVisibleValue) * height,
      };
    });

    if (points.length === 0)
      return { area: "", line: "", totalWidth: 0, points: [] };

    const generateSmoothPath = (points: { x: number; y: number }[]) => {
      if (points.length < 2) {
        return `M ${points[0].x},${points[0].y}`;
      }

      let path = `M ${points[0].x},${points[0].y}`;

      for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[Math.max(0, i - 1)];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[Math.min(points.length - 1, i + 2)];

        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;

        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
      }

      return path;
    };

    const linePath = generateSmoothPath(points);
    const areaPath =
      linePath +
      ` L ${points[points.length - 1].x},${height}` +
      ` L ${points[0].x},${height}` +
      ` Z`;

    return {
      area: areaPath,
      line: linePath,
      points,
    };
  }, [
    showTrendLine,
    visibleData,
    visibleWeeks,
    bars.length,
    containerWidth,
    niceMaxVisibleValue,
  ]);

  useEffect(() => {
    if (!containerEl) return;

    const observer = new ResizeObserver((entries) => {
      const [entry = null] = entries;
      if (!entry) return;

      const [size = null] = entry.borderBoxSize;
      if (!size) return;

      setContainerWidth(size.inlineSize);
      setResizeCounter((prev) => prev + 1);
    });

    observer.observe(containerEl);

    return () => {
      observer.disconnect();
    };
  }, [containerEl]);

  useEffect(() => {
    if (!onVisibleWeeksChange) return;
    onVisibleWeeksChange(visibleWeeks);
  }, [visibleWeeks, onVisibleWeeksChange]);

  return (
    <div
      className="text-theme-on-background relative mb-12"
      data-lso-weekly-graph="WeeklyGraph"
    >
      <div className="relative flex">
        {/* Y-axis */}
        <div className="text-theme-on-background-variant min-w-7 shrink-0 grow-0 border-y border-transparent pr-4 text-right text-[10px]">
          {/* Reserve width for the highest value to avoid resize observer loops */}
          <div className="invisible h-0" aria-hidden="true">
            {formatNumber(niceMaxValue)}
          </div>

          <div
            className="flex flex-col justify-between"
            style={{ height: GRAPH_HEIGHT }}
          >
            {ticks.length > 0 ? (
              ticks.map((value, i) => (
                <div key={i} className="flex h-0 flex-col">
                  <span className="-translate-y-1/2">
                    {formatNumber(value)}
                  </span>
                </div>
              ))
            ) : (
              <div className="flex h-0 flex-col">
                <span className="-translate-y-1/2">{formatNumber(0)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-theme-outline-variant relative grid w-full min-w-0 shrink grow grid-cols-[100%] grid-rows-[100%] border border-r-0">
          <div
            className="col-start-1 row-start-1 flex w-full flex-col justify-evenly"
            style={{ height: GRAPH_HEIGHT }}
          >
            {ticks.length > 2
              ? ticks.slice(1, -1).map((_, i) => (
                  <div key={i} className="flex h-0 flex-col justify-center">
                    <div className="border-theme-outline-variant border-b" />
                  </div>
                ))
              : null}
          </div>

          <div
            ref={setContainerEl}
            className="group/graph z-5 col-start-1 row-start-1 grid *:col-start-1 *:row-start-1 *:min-w-0"
          >
            {showTrendLine && (
              <div className="pointer-events-none relative z-20 overflow-hidden">
                <svg
                  key={`trend-svg-${resizeCounter}`}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${containerWidth}px`,
                    height: `${GRAPH_HEIGHT}px`,
                  }}
                  viewBox={`0 0 ${containerWidth} ${GRAPH_HEIGHT}`}
                  preserveAspectRatio="none"
                >
                  <defs>
                    <linearGradient
                      id="trendLineAreaGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        stopColor={"var(--theme-primary)"}
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="70%"
                        stopColor={"var(--theme-primary)"}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="100%"
                        stopColor={"var(--theme-primary)"}
                        className="stop-theme-on-background"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>

                  <path
                    d={trendLinePathData.area}
                    fill="url(#trendLineAreaGradient)"
                    opacity={0.6}
                  />

                  <path
                    d={trendLinePathData.line}
                    fill="none"
                    stroke="bg-theme-on-background-hover"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="stroke-theme-primary"
                  />
                </svg>
              </div>
            )}

            <div className="relative flex">
              {bars.map((bar, index) => {
                const barHasCurrentWeekCursor =
                  showCurrentWeekCursor && bar.isCurrentWeek;

                return (
                  <div
                    key={`${bar.week.start}`}
                    className="group/bar-outer border-theme-outline-variant relative flex grow border-r"
                    style={{
                      width: BAR_WIDTH,
                    }}
                  >
                    <div
                      className="group/bar hover:bg-theme-on-background-hover has-selected:opacity-100 relative flex h-full w-full transition-[background-color,opacity] group-hover/graph:opacity-75 hover:opacity-100 in-[.group\/graph:has(.group\/bar_*[data-selected])]:opacity-75"
                      data-lso-weekly-graph-bar
                      data-lso-current-week={bar.isCurrentWeek || undefined}
                      data-lso-previous-week={bar.isPreviousWeek || undefined}
                      data-lso-next-week={bar.isNextWeek || undefined}
                    >
                      <div className="group-odd/bar-outer:to-theme-on-background/5 grid w-full *:col-start-1 *:row-start-1 group-odd/bar-outer:bg-linear-to-b dark:group-odd/bar-outer:bg-linear-to-t">
                        <AnimatePresence initial={false}>
                          {bar.data && (
                            <motion.div
                              className="flex h-full w-full flex-col items-center justify-end"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                            >
                              {renderBar({
                                week: bar.week,
                                data: bar.data,
                                maxValue: niceMaxVisibleValue,
                                index,
                              })}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {bar.isFirstWeek && (
                        <div
                          className={twMerge(
                            String.raw`pointer-events-none absolute top-full left-0 -translate-x-1/2 pt-4 text-[11px] font-bold text-nowrap opacity-100 transition-opacity [.group\/graph:has(.group\/bar:hover,.group\/bar_*[data-selected],.group\/current-week-cursor:hover)_*]:opacity-0`,
                            barHasCurrentWeekCursor && "opacity-0",
                          )}
                        >
                          {bar.formattedStartDate}
                        </div>
                      )}

                      {bar.isLastWeek && (
                        <div
                          className={twMerge(
                            String.raw`pointer-events-none absolute top-full right-0 translate-x-1/2 pt-4 text-[11px] font-bold text-nowrap opacity-100 transition-opacity [.group\/graph:has(.group\/bar:hover,.group\/bar_*[data-selected],.group\/current-week-cursor:hover)_*]:opacity-0`,
                            barHasCurrentWeekCursor && "opacity-0",
                          )}
                        >
                          {bar.formattedEndDate}
                        </div>
                      )}

                      <div className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 pt-4 text-[11px] font-bold text-nowrap opacity-0 transition-opacity group-hover/bar:opacity-100 [.group\/graph:not(:has(.group\/bar:hover,.group\/current-week-cursor:hover))_.group\/bar:has(*[data-selected])_*]:opacity-100">
                        {bar.formattedPeriod}
                      </div>
                    </div>

                    {barHasCurrentWeekCursor && (
                      <CurrentWeekCursor
                        className={twMerge(
                          "transition-opacity",
                          !hasData &&
                            !isLoading &&
                            "pointer-events-none opacity-0!",
                        )}
                        date={UTCToLocalDate(currentDate)}
                        showDate={true}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            <AnimatePresence>
              {(isLoading || !hasData) && (
                <motion.div
                  className="relative size-full border-r border-r-transparent"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="from-theme-background/40 to-theme-background/90 absolute inset-0 bg-linear-to-b" />

                  <div className="relative flex size-full flex-col items-center justify-center gap-y-4">
                    <div className="grid *:col-start-1 *:row-start-1">
                      <DashedRings />
                      <div className="flex items-center justify-center">
                        <div className="bg-theme-primary/30 border-theme-primary/40 flex size-15 items-center justify-center rounded-full border-2 border-solid backdrop-blur-lg">
                          <Loader className="text-theme-primary" size={30} />
                        </div>
                      </div>
                    </div>

                    <div className="text-theme-on-background-variant grid items-center justify-items-center text-xs font-medium *:col-start-1 *:row-start-1">
                      <AnimatePresence>
                        {isLoading ? (
                          <motion.span
                            key={"loading"}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            {loadingText}
                          </motion.span>
                        ) : (
                          <motion.span
                            key="noData"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                          >
                            {noDataText}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {isXLBreakpoint && (
        <>
          {canGoBack && (
            <RACButton
              onPress={() => setWeekOffset((prev) => prev - 1)}
              className={composeTailwindRenderProps(
                focusRing,
                "absolute top-1/2 -left-10 z-10 -translate-y-1/2 cursor-pointer rounded-full border border-transparent p-1.5 hover:border-gray-700 hover:bg-white/5 active:bg-white/10",
              )}
              aria-label="Forrige uge"
            >
              <ChevronLeftIcon className="size-5" />
            </RACButton>
          )}

          {canGoForward && (
            <RACButton
              aria-label="Næste uge"
              onPress={() => setWeekOffset((prev) => prev + 1)}
              className={composeTailwindRenderProps(
                focusRing,
                "absolute top-1/2 -right-10 z-10 -translate-y-1/2 cursor-pointer rounded-full border border-transparent p-1.5 hover:border-gray-700 hover:bg-white/5 active:bg-white/10",
              )}
            >
              <ChevronRightIcon className="size-5" />
            </RACButton>
          )}
        </>
      )}
    </div>
  );
}

function CurrentWeekCursor({
  className,
  date,
  showDate,
}: {
  className?: string;
  date: Date;
  showDate: boolean;
}) {
  // Get current day of the week, and adjust it to start from Monday instead of Sunday.
  const currentDay = (date.getDay() + 6) % 7;

  // Calculate the offset for the cursor based on the current day
  const dayOfWeekOffset = BAR_WIDTH / 7;

  // Calculate the position of the cursor based on the current day
  const cursorPosition = currentDay * dayOfWeekOffset;

  const formattedDate = useMemo(() => formatDateAndMonth(date), [date]);

  return (
    <div
      className={twMerge(
        "group/current-week-cursor pointer-events-none absolute inset-y-0 z-100 flex h-full w-3 -translate-x-1/2 flex-col items-center transition-opacity group-hover/graph:opacity-75 hover:opacity-100",
        className,
      )}
      style={{ left: cursorPosition }}
    >
      <svg
        width={13}
        height={13}
        viewBox="0 0 13 13"
        className="-mb-1 shrink-0 fill-black dark:fill-white"
      >
        <path d="M0 0 L6.5 6.5 L13 0" />
      </svg>

      <svg width="1" height="100%" className="shrink grow">
        <line
          x1="50%"
          x2="50%"
          y1="0"
          y2="100%"
          className="stroke-black dark:stroke-white"
          strokeWidth="100"
          strokeDasharray="4 3"
        />
      </svg>

      {showDate && (
        <div className="pointer-events-none absolute top-full left-1/2 -translate-x-1/2 pt-4 text-center text-[11px] font-bold text-nowrap transition-opacity group-hover/current-week-cursor:opacity-100 in-[.group\/graph:has(.group\/bar:hover,.group\/bar_*[data-selected])]:opacity-0">
          <div>{formattedDate}</div>
          <div>i dag</div>
        </div>
      )}
    </div>
  );
}
